import React, { useCallback, useEffect, useState } from "react"
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Loader } from "../../../global/styles";
import { Row } from "../../../global/components/row";
import Switch from "../../../global/components/switch";
import { get, isEqual, orderBy, set, size } from "../../../global/lib/lodash";
import { tradeStyles } from "./styles";
import clsx from "clsx";
import { Button } from '../../../global/components/Button';
import { TextField, Icon } from "@material-ui/core";
import colors from "../../../global/styles/colors";
import sendData from "../../../redux/actions/sendData";
import SnackAlerts from '../../../global/components/snack-alerts';
import { Text } from "../../../global/components/text";

export const TradesList = props => {
  const classes = tradeStyles();
  const [alerts, setAlerts] = useState();
  const [state, setState] = useState({
    isEdit: false,
    data: [],
    saving: false,
    sort: {
      name: 'asc',
      customName: 'asc',
      clientKey: 'asc',
    }
  })

  const sortData = useCallback(d => {
    const dd = orderBy(d, 'defaultName', get(state, 'sort.name'));
    return dd;
  }, [state.sort]);

  const trades = useRetrieveData({ url: '/trade-types' }, {
    version: 'v2',
    build: ['tradeTypes'],
    // params: {
    //   filter: { enabled: false }
    // }
  })

  useEffect(() => {
    setState(s => ({ ...s, data: sortData(trades.data) }));
  }, [trades.data]);

  useEffect(() => {
    if (!state.isEdit && size(trades.data)) {
      setState(s => ({ ...s, data: sortData([...trades.data]) }));
    }
  }, [state.isEdit])

  const editRow = (r, i, update = true) => {
    set(state.data, i, r);
    if (update) {
      setState(s => ({ ...s, data: [...state.data] }));
    }
  };

  const save = async () => {
    try {
      setState(s => ({ ...s, saving: true }));
      const diffs = state.data.filter((d, i) => {
        const comp = trades.data[i];
        return !isEqual(d, comp)
      });
  
      await Promise.all(diffs.map(async d => {
        await sendData({
          url: `/trade-types/${d.id}`,
          method: 'patch',
          data: {
            data: {
              id: d.id,
              type: 'trade-types',
              attributes: {
                name: get(d, 'name'),
                enabled: !!get(d, 'enabled'),
                client_key: get(d, 'clientKey'),
              }
            }
          }
        }, { version: 'v2' })
      }))

      trades.update();
      setState(s => ({ ...s, isEdit: false }));
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState(s => ({ ...s, saving: false }));
    }
  }

  const COLUMNS = [
    {
      headerName: 'Enabled',
      style: { width: '10%', minWidth: 55 },
      renderCell: (c, i) => {
        const checked = !!get(c, 'enabled');
        
        const p = state.isEdit ? {
          defaultChecked: checked,
          onChange: e => editRow({ ...c, enabled: e.target.checked }, i, true),
        } : { checked };

        return (
          <div>
            <Switch size="small" {...p} />
          </div>
        )
      }
    },
    {
      headerName: 'Name',
      style: { width: '30%' },
      sort: () => {
        const opts = {
          asc: 'desc',
          desc: 'asc',
        }
        const dir = get(opts, get(state, 'sort.name'));
        const ord = orderBy(state.data, 'defaultName', dir);
        setState(s => ({ ...s, data: ord, sort: { ...s.sort, name: dir } }))
      },
      sortIcon: () => {
        return get(state, 'sort.name') === 'desc' ? 'arrow_drop_up' : 'arrow_drop_down';
      },
      renderCell: (c, i) => {
        return <Text>{get(c, 'defaultName')}</Text>
      }
    },
    {
      headerName: 'Custom Name',
      style: { width: '30%' },
      sort: () => {
        const opts = {
          asc: 'desc',
          desc: 'asc',
        }
        const dir = get(opts, get(state, 'sort.customName'));
        const ord = orderBy(state.data, 'name', dir);
        setState(s => ({ ...s, data: ord, sort: { ...s.sort, customName: dir } }))
      },
      sortIcon: () => {
        return get(state, 'sort.customName') === 'desc' ? 'arrow_drop_up' : 'arrow_drop_down';
      },
      renderCell: (c, i) => {
        const val = get(c, 'name');
        if (state.isEdit) {
          const disabled = !get(c, 'enabled');
          return <TextField disabled={disabled} size="small" variant="outlined" defaultValue={val} onChange={e => editRow({ ...c, name: e.target.value }, i)} />
        }
        return <Text>{val}</Text>
      }
    },
    {
      headerName: 'Key ID',
      style: { width: '30%' },
      sort: () => {
        const opts = {
          asc: 'desc',
          desc: 'asc',
        }
        const dir = get(opts, get(state, 'sort.clientKey'));
        const ord = orderBy(state.data, 'clientKey', dir);
        setState(s => ({ ...s, data: ord, sort: { ...s.sort, clientKey: dir } }))
      },
      sortIcon: () => {
        return get(state, 'sort.clientKey') === 'desc' ? 'arrow_drop_up' : 'arrow_drop_down';
      },
      renderCell: (c, i) => {
        const val = get(c, 'clientKey');
        if (state.isEdit) {
          const disabled = !get(c, 'enabled');
          return <TextField disabled={disabled} size="small" variant="outlined" defaultValue={val} onChange={e => editRow({ ...c, clientKey: e.target.value }, i)} />
        }
        return <Text>{val}</Text>
      }
    },
  ]

  //#region renders
  const renderTradeRow = (trade, i) => {
    if (!state.isEdit && !get(trade, 'enabled')) { return null }
    return (
      <Row className={classes.row} key={`${!!state.isEdit}-${trade.id}`}>
        {COLUMNS.map((c, _i) => {
          return (
            <div key={_i} className={classes.cell} style={c.style}>
              {c.renderCell(trade, i)}
            </div>
          )
        })}
      </Row>
    )
  }

  const renderHeader = () => {
    return (
      <Row className={clsx(classes.header, classes.row)}>
        {COLUMNS.map(c => {
          return (
            <Row className={classes.cell} style={{ ...c.style, fontWeight: 'bold', cursor: c.sort ? 'pointer' : 'default' }} onClick={c.sort}>
              {c.headerName}
              {c.sort && <Icon>{c.sortIcon()}</Icon>}
            </Row>
          )
        })}
      </Row>
    )
  }
  //#endregion

  return (
    <div>
      <Row style={{ padding: `16px 0` }}>
        <Button icon={state.isEdit ? "close" : "edit"} bgColor={state.isEdit ? colors.danger : undefined} title="EDIT LIST" onClick={() => setState(s => ({ ...s, isEdit: !s.isEdit }))} />
        {state.isEdit && <Button title="SAVE" icon="save" onClick={save} style={{ marginLeft: 8 }} />}
      </Row>

      <div>
        {renderHeader()}
        <div>
          {trades.loading && <Loader />}
          {!trades.loading && !state.saving && state.data.map(renderTradeRow)}
        </div>
      </div>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  )
}